import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, Truck, MapPin, Package, RefreshCw, Clock,
  ArrowDownToLine, ArrowUpFromLine, Wrench, Layers, ChevronDown, ChevronUp, X,
  Home as HomeIcon, Calendar, AlertTriangle, Briefcase,
} from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import api from '../../api/auth.api';

/**
 * Trucker Day (v2, TEST MODE) — GPS-first trucker clock-in page.
 *
 * Clock in -> pick your truck -> the haul detector builds the day (stops, loads,
 * drops) from GPS. Driver taps on nearby equipment are annotations that confirm
 * the GPS log; at end of day the haul log wins. Starting a shift here does NOT
 * create a TIME_ENTRIES row — zero payroll effect while we validate.
 */

interface Shift {
  shift_id: number;
  employee_id: number;
  truck_equipment_id: number;
  started_at: string;
  ended_at?: string | null;
  truck_name?: string;
  truck_number?: string;
  is_hauler?: boolean;
}

interface TruckOption {
  equipment_id: number;
  bradanna_equipment_number: string;
  equipment_name: string;
  equipment_type: string;
  is_hauler: boolean;
  has_tracker: number;
}

interface StopMovement { movement_id: number; equipment_id: number; name: string; status: string; detected_at: string; }

interface HaulStop {
  stop_id: number; hauler_id: number; name: string; job_id: number | null; place_id: number | null;
  arrived_at: string; departed_at: string | null; dwell_minutes: number | null; status: string;
  loaded: StopMovement[]; dropped: StopMovement[];
}

interface DriverEvent {
  movement_id: number; equipment_id: number | null; action: 'load' | 'unload'; timestamp: string;
  is_rental: boolean; rental_company?: string; rental_equipment_type?: string; rental_description?: string;
  notes?: string; equipment_name?: string; bradanna_equipment_number?: string; job_name?: string;
}

interface DumpLoad { load_id: number; material_type: string; load_count: number; timestamp: string; job_name?: string; notes?: string; }

interface GpsEvent { movement_id: number; name: string; status: string; detected_at: string; action: 'load' | 'unload'; job_name: string | null; }

interface DayData {
  shifts: Shift[];
  stops: HaulStop[];
  gps_events?: GpsEvent[];
  driver_events: DriverEvent[];
  dump_loads: DumpLoad[];
  live: { moving: boolean; on_trailer: { equipment_id: number; name: string }[] } | null;
}

interface NearbyEquipment {
  equipment_id: number; bradanna_equipment_number: string; equipment_name: string;
  equipment_type: string; distance_m: number; last_fix_at: string | null;
  anchored_hauler_id: number | null;
}

interface DriverOption { employee_id: number; first_name: string; last_name: string; }

const fmtTime = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) : '—';

// Fleet-page naming for machine pills: drop the leading unit number the API
// prepends, then move the model token to the front ("Cat 270" → "270 Cat").
const pillName = (raw?: string): string => {
  const parts = (raw || '').trim().split(/\s+/);
  if (parts.length > 1 && /^\d{3,}$/.test(parts[0])) parts.shift();
  const last = parts[parts.length - 1];
  if (parts.length >= 2 && /\d/.test(last)) return [last, ...parts.slice(0, -1)].join(' ');
  return parts.join(' ');
};

const fmtDist = (m: number) => (m < 400 ? `${m} m` : `${(m / 1609.34).toFixed(1)} mi`);

const getPosition = (): Promise<{ lat: number; lng: number }> =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('No GPS available'));
    // A minute-old fix is plenty for tagging a movement — keeps Save snappy
    // instead of stalling up to 12s on a fresh GPS acquisition.
    navigator.geolocation.getCurrentPosition(
      p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      e => reject(new Error(e.message || 'GPS failed')),
      { enableHighAccuracy: true, timeout: 6000, maximumAge: 60000 },
    );
  });

const card: React.CSSProperties = {
  background: '#fff', borderRadius: 12, padding: 16, marginBottom: 12,
  boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb',
};
const bigBtn = (bg: string): React.CSSProperties => ({
  width: '100%', padding: '14px 16px', borderRadius: 10, border: 'none', background: bg,
  color: '#fff', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center',
  justifyContent: 'center', gap: 8, cursor: 'pointer',
});
const chip = (bg: string, fg: string): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 4, background: bg, color: fg,
  borderRadius: 999, padding: '3px 10px', fontSize: 13, fontWeight: 600, margin: '2px 4px 2px 0',
});
const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
  display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
};
const sheet: React.CSSProperties = {
  background: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 560,
  maxHeight: '85vh', overflowY: 'auto', padding: 16,
};
const input: React.CSSProperties = {
  width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db',
  fontSize: 15, marginBottom: 10, boxSizing: 'border-box',
};

interface WeeklyData { weekStart: string; dailyHours: { work_date: string; total_hours: number; entries: number }[]; weekTotal: number; }

export const TruckerDay: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const me = auth?.user;
  const amAdmin = me?.role === 'admin' || !!me?.is_super_admin;
  const isTrucker = me?.role === 'truck_driver';

  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const [weekly, setWeekly] = useState<WeeklyData | null>(null);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const openWeekly = async () => {
    setWeeklyOpen(true); setWeeklyLoading(true);
    try {
      const r = await api.get('/api/cdl/log/weekly');
      setWeekly(r.data?.data ?? null);
    } catch { setWeekly(null); }
    finally { setWeeklyLoading(false); }
  };

  const [driverId, setDriverId] = useState<number | null>(null);
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [shift, setShift] = useState<Shift | null>(null);
  const [day, setDay] = useState<DayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; err?: boolean } | null>(null);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [trucks, setTrucks] = useState<{ featured: TruckOption[]; others: TruckOption[] } | null>(null);
  const [showOthers, setShowOthers] = useState(false);

  const [nearby, setNearby] = useState<NearbyEquipment[] | null>(null);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [confirmMove, setConfirmMove] = useState<{ eq: NearbyEquipment; action: 'load' | 'unload' } | null>(null);

  const [rentalOpen, setRentalOpen] = useState(false);
  const [rental, setRental] = useState({ action: 'load', company: '', type: '', description: '' });
  const [miscOpen, setMiscOpen] = useState(false);
  const [misc, setMisc] = useState({ action: 'load', description: '', notes: '' });
  const [dumpOpen, setDumpOpen] = useState(false);
  const [dump, setDump] = useState({ job_id: '', material: '', count: '1', notes: '' });
  const [jobs, setJobs] = useState<{ job_id: number; job_name: string }[]>([]);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const say = (msg: string, err = false) => {
    setToast({ msg, err });
    setTimeout(() => setToast(null), 4000);
  };

  // Resolve who we're acting for: drivers = themselves; admins get a picker.
  useEffect(() => {
    if (!me) return;
    if (me.role === 'truck_driver') { setDriverId(me.employee_id); return; }
    (async () => {
      try {
        const r = await api.get('/api/employees');
        const list: DriverOption[] = (r.data?.data || [])
          .filter((e: { role?: string; is_active?: boolean }) => e.role === 'truck_driver' && e.is_active !== false);
        setDrivers(list);
        setDriverId(list[0]?.employee_id ?? me.employee_id);
      } catch { setDriverId(me.employee_id); }
    })();
  }, [me]);

  const refresh = useCallback(async (quiet = false) => {
    if (!driverId) return;
    if (!quiet) setLoading(true);
    try {
      const [cur, d] = await Promise.all([
        api.get(`/api/cdl/v2/shift/current?employee_id=${driverId}`),
        api.get(`/api/cdl/v2/day?employee_id=${driverId}`),
      ]);
      setShift(cur.data?.data ?? null);
      setDay(d.data?.data ?? null);
    } catch {
      if (!quiet) say('Could not load day — check connection', true);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [driverId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll while a shift is open so the GPS timeline builds itself on screen.
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (shift) pollRef.current = setInterval(() => refresh(true), 60_000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [shift, refresh]);

  const openPicker = async () => {
    setPickerOpen(true);
    if (!trucks) {
      try {
        const r = await api.get('/api/cdl/v2/trucks');
        setTrucks(r.data?.data ?? { featured: [], others: [] });
      } catch { say('Could not load trucks', true); setPickerOpen(false); }
    }
  };

  // Also the mid-day SWITCH: starting a shift while one is open swaps trucks
  // server-side (old shift closes, no double clock-in, timecard splitter walks
  // both trucks in their own windows).
  const startDay = async (truck: TruckOption) => {
    const switching = !!shift;
    setBusy(true);
    try {
      let pos: { lat: number; lng: number } | null = null;
      try { pos = await getPosition(); } catch { /* start anyway; GPS optional here */ }
      await api.post('/api/cdl/v2/shift/start', {
        truck_equipment_id: truck.equipment_id, employee_id: driverId,
        lat: pos?.lat, lng: pos?.lng,
      });
      setPickerOpen(false); setShowOthers(false);
      say(switching ? `Switched to ${truck.equipment_name}` : `Shift started on ${truck.equipment_name}`);
      await refresh();
    } catch { say(switching ? 'Could not switch trucks' : 'Could not start shift', true); }
    finally { setBusy(false); }
  };

  const endDay = async () => {
    if (!window.confirm('End the day? This clocks you out and builds your timecard from the GPS log.')) return;
    setBusy(true);
    try {
      await api.post('/api/cdl/v2/shift/end', { employee_id: driverId });
      say('Shift ended');
      setNearby(null);
      await refresh();
    } catch { say('Could not end shift', true); }
    finally { setBusy(false); }
  };

  const loadNearby = async () => {
    setNearbyLoading(true);
    try {
      const pos = await getPosition();
      const r = await api.get(`/api/cdl/v2/equipment/nearby?lat=${pos.lat}&lng=${pos.lng}`);
      setNearby(r.data?.data?.equipment ?? []);
    } catch (e) { say(e instanceof Error ? e.message : 'Could not check nearby equipment', true); }
    finally { setNearbyLoading(false); }
  };

  // Returns true on success so modals can stay OPEN on failure (closing
  // instantly with a maybe-12s GPS wait behind it read as "the button is dead").
  const postMovement = async (body: Record<string, unknown>, okMsg: string): Promise<boolean> => {
    setBusy(true);
    try {
      let pos: { lat: number; lng: number } | null = null;
      try { pos = await getPosition(); } catch { /* record without coords */ }
      await api.post('/api/cdl/v2/movement', { ...body, employee_id: driverId, lat: pos?.lat, lng: pos?.lng });
      say(okMsg);
      refresh(true);
      return true;
    } catch { say('Could not save — try again', true); return false; }
    finally { setBusy(false); }
  };

  const submitDump = async () => {
    if (!dump.job_id) { say('Pick the job this load bills to', true); return; }
    if (!dump.material.trim()) { say('Enter the material first', true); return; }
    setBusy(true);
    try {
      let pos: { lat: number; lng: number } | null = null;
      try { pos = await getPosition(); } catch { /* optional */ }
      await api.post('/api/cdl/dump-load', {
        job_id: Number(dump.job_id), material_type: dump.material,
        load_count: Number(dump.count) || 1, notes: dump.notes || undefined,
        lat: pos?.lat, lng: pos?.lng,
      });
      setDumpOpen(false); setDump({ job_id: '', material: '', count: '1', notes: '' });
      say('Dump load recorded');
      refresh(true);
    } catch { say('Could not save dump load', true); }
    finally { setBusy(false); }
  };

  const openDump = async () => {
    setDumpOpen(true);
    if (!jobs.length) {
      try { const r = await api.get('/api/cdl/jobs'); setJobs(r.data?.data || []); } catch { /* keep empty */ }
    }
  };

  // Merge GPS stops + driver taps + dump loads into one chronological timeline.
  type TimelineItem =
    | { kind: 'stop'; time: string; stop: HaulStop }
    | { kind: 'gps'; time: string; ge: GpsEvent }
    | { kind: 'driver'; time: string; ev: DriverEvent }
    | { kind: 'dump'; time: string; dl: DumpLoad };
  const timeline: TimelineItem[] = [
    ...(day?.stops || []).map(s => ({ kind: 'stop' as const, time: s.arrived_at, stop: s })),
    ...(day?.gps_events || []).map(ge => ({ kind: 'gps' as const, time: ge.detected_at, ge })),
    ...(day?.driver_events || []).map(ev => ({ kind: 'driver' as const, time: ev.timestamp, ev })),
    ...(day?.dump_loads || []).map(dl => ({ kind: 'dump' as const, time: dl.timestamp, dl })),
  ].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

  const evLabel = (ev: DriverEvent) => {
    if (ev.is_rental && ev.rental_company === 'MISC') return ev.rental_description || 'Parts / materials';
    if (ev.is_rental) return `${ev.rental_company || 'Rental'} ${ev.rental_equipment_type || ''}`.trim();
    return `${ev.bradanna_equipment_number ? ev.bradanna_equipment_number + ' ' : ''}${ev.equipment_name || 'Equipment'}`;
  };

  if (!me) return null;

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: '12px 12px 90px' }}>
      {/* Admin driver picker */}
      {amAdmin && drivers.length > 0 && (
        <div style={{ ...card, padding: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280' }}>DRIVER</label>
          <select
            value={driverId ?? ''}
            onChange={e => setDriverId(Number(e.target.value))}
            style={{ ...input, marginBottom: 0, marginTop: 4 }}
          >
            {drivers.map(d => (
              <option key={d.employee_id} value={d.employee_id}>{d.first_name} {d.last_name}</option>
            ))}
            {me && !drivers.some(d => d.employee_id === me.employee_id) && (
              <option value={me.employee_id}>{me.first_name} {me.last_name} (me)</option>
            )}
          </select>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Loader2 size={32} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : !shift ? (
        /* ------- NOT ON SHIFT ------- */
        <div style={card}>
          <h2 style={{ margin: '0 0 6px', fontSize: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Truck size={22} /> Start your day
          </h2>
          <p style={{ margin: '0 0 14px', color: '#6b7280', fontSize: 14 }}>
            This clocks you in and picks your truck. GPS builds your timecard from there.
          </p>
          <button style={bigBtn('#16a34a')} disabled={busy} onClick={openPicker}>
            {busy ? <Loader2 size={18} /> : '🟢'} Start Day
          </button>
        </div>
      ) : (
        /* ------- ON SHIFT ------- */
        <>
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280' }}>ON SHIFT — {fmtTime(shift.started_at)}</div>
                <div style={{ fontSize: 22, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Truck size={22} color="#2563eb" /> {shift.truck_name}
                  <button
                    onClick={openPicker} disabled={busy}
                    style={{
                      border: '1px solid #d1d5db', background: '#fff', borderRadius: 8,
                      padding: '5px 12px', fontSize: 13, fontWeight: 700, color: '#2563eb', cursor: 'pointer',
                    }}
                  >
                    Switch Truck
                  </button>
                </div>
              </div>
              {day?.live && (
                <span style={chip(day.live.moving ? '#dcfce7' : '#f3f4f6', day.live.moving ? '#166534' : '#374151')}>
                  {day.live.moving ? 'Moving' : 'Stopped'}
                </span>
              )}
            </div>
            {day?.live && day.live.on_trailer.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 4 }}>ON TRAILER NOW</div>
                {day.live.on_trailer.map(t => (
                  <span key={t.equipment_id} style={chip('#dbeafe', '#1e40af')}><Package size={13} /> {pillName(t.name)}</span>
                ))}
              </div>
            )}
            <button style={{ ...bigBtn('#dc2626'), marginTop: 14 }} disabled={busy} onClick={endDay}>
              🔴 End Day
            </button>
          </div>

          {/* Nearby equipment */}
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0, fontSize: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
                <MapPin size={18} /> Equipment near me
              </h3>
              <button
                onClick={loadNearby} disabled={nearbyLoading}
                style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 8, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}
              >
                <RefreshCw size={15} style={nearbyLoading ? { animation: 'spin 1s linear infinite' } : undefined} />
                {nearby === null ? 'Check' : 'Refresh'}
              </button>
            </div>
            {nearby === null ? (
              <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>
                Tap Check to see tracked machines within a half mile — pick up or drop off from the list.
              </p>
            ) : nearby.length === 0 ? (
              <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>No tracked equipment within a half mile.</p>
            ) : nearby.map(eq => {
              const onMyTrailer = eq.anchored_hauler_id === shift.truck_equipment_id;
              return (
                <div key={eq.equipment_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderTop: '1px solid #f3f4f6' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>
                      {eq.bradanna_equipment_number} {eq.equipment_name}
                      {onMyTrailer && <span style={{ ...chip('#dbeafe', '#1e40af'), marginLeft: 6 }}>on trailer</span>}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{eq.equipment_type} · {fmtDist(eq.distance_m)} away</div>
                  </div>
                  <button
                    disabled={busy}
                    onClick={() => setConfirmMove({ eq, action: onMyTrailer ? 'unload' : 'load' })}
                    style={{
                      border: 'none', borderRadius: 8, padding: '8px 12px', fontWeight: 700, fontSize: 13,
                      cursor: 'pointer', color: '#fff', background: onMyTrailer ? '#d97706' : '#2563eb',
                      display: 'flex', alignItems: 'center', gap: 5,
                    }}
                  >
                    {onMyTrailer ? <><ArrowDownToLine size={15} /> Drop Off</> : <><ArrowUpFromLine size={15} /> Pick Up</>}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Manual adds — no GPS signal for these, so the driver logs them */}
          <div style={{ ...card, display: 'flex', gap: 8 }}>
            <button onClick={() => setRentalOpen(true)} style={{ ...bigBtn('#7c3aed'), fontSize: 13, padding: '10px 6px' }}>
              <Wrench size={16} /> Rental
            </button>
            <button onClick={() => setMiscOpen(true)} style={{ ...bigBtn('#0891b2'), fontSize: 13, padding: '10px 6px' }}>
              <Package size={16} /> Parts / Mat'l
            </button>
            <button onClick={openDump} style={{ ...bigBtn('#65a30d'), fontSize: 13, padding: '10px 6px' }}>
              <Layers size={16} /> Dump Loads
            </button>
          </div>
        </>
      )}

      {/* ------- TIMELINE ------- */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Clock size={18} /> Today
          </h3>
          <button
            onClick={() => refresh(true)}
            style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}
          >
            <RefreshCw size={15} />
          </button>
        </div>
        {timeline.length === 0 ? (
          <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>
            Nothing yet. Once the truck is moving, stops and loads show up here on their own.
          </p>
        ) : timeline.map(item => {
          if (item.kind === 'stop') {
            const s = item.stop;
            return (
              <div key={`s${s.stop_id}`} style={{ borderLeft: '3px solid #2563eb', padding: '6px 0 6px 12px', marginBottom: 10 }}>
                <div style={{ fontWeight: 800, fontSize: 15 }}>{s.name}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  {fmtTime(s.arrived_at)} – {s.departed_at ? fmtTime(s.departed_at) : 'still here'}
                  {s.dwell_minutes != null && ` · ${s.dwell_minutes} min`}
                  {' · GPS'}
                </div>
                <div>
                  {/* Drops first — that's the physical order at a stop */}
                  {s.dropped.map(m => (
                    <span key={m.movement_id} style={chip('#fef3c7', '#92400e')}>
                      <ArrowDownToLine size={12} /> {pillName(m.name)}{m.status === 'provisional' ? ' ?' : ''}
                    </span>
                  ))}
                  {s.loaded.map(m => (
                    <span key={m.movement_id} style={chip('#dbeafe', '#1e40af')}>
                      <ArrowUpFromLine size={12} /> {pillName(m.name)}{m.status === 'provisional' ? ' ?' : ''}
                    </span>
                  ))}
                </div>
              </div>
            );
          }
          if (item.kind === 'gps') {
            const ge = item.ge;
            return (
              <div key={`g${ge.movement_id}`} style={{ borderLeft: '3px solid #2563eb', padding: '6px 0 6px 12px', marginBottom: 10 }}>
                <div style={{ fontWeight: 800, fontSize: 15 }}>{ge.job_name || 'Quick stop'}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{fmtTime(ge.detected_at)} · quick stop</div>
                <div>
                  <span style={chip(ge.action === 'load' ? '#dbeafe' : '#fef3c7', ge.action === 'load' ? '#1e40af' : '#92400e')}>
                    <ArrowUpFromLine size={12} style={{ display: ge.action === 'load' ? undefined : 'none' }} />
                    <ArrowDownToLine size={12} style={{ display: ge.action === 'unload' ? undefined : 'none' }} />
                    {' '}{pillName(ge.name)}{ge.status === 'provisional' ? ' ?' : ''}
                  </span>
                </div>
              </div>
            );
          }
          if (item.kind === 'driver') {
            const ev = item.ev;
            return (
              <div key={`d${ev.movement_id}`} style={{ borderLeft: '3px solid #9ca3af', padding: '4px 0 4px 12px', marginBottom: 8, fontSize: 13 }}>
                <span style={{ fontWeight: 700 }}>{ev.action === 'load' ? 'Picked up' : 'Dropped off'} {evLabel(ev)}</span>
                <span style={{ color: '#6b7280' }}> · {fmtTime(ev.timestamp)} · you{ev.job_name ? ` · ${ev.job_name}` : ''}</span>
              </div>
            );
          }
          const dl = item.dl;
          return (
            <div key={`l${dl.load_id}`} style={{ borderLeft: '3px solid #65a30d', padding: '4px 0 4px 12px', marginBottom: 8, fontSize: 13 }}>
              <span style={{ fontWeight: 700 }}>{dl.load_count}× {dl.material_type}</span>
              <span style={{ color: '#6b7280' }}> · {fmtTime(dl.timestamp)}{dl.job_name ? ` · ${dl.job_name}` : ''}</span>
            </div>
          );
        })}
      </div>

      {/* ------- MODALS ------- */}
      {pickerOpen && (
        <div style={overlay} onClick={() => setPickerOpen(false)}>
          <div style={sheet} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h3 style={{ margin: 0 }}>{shift ? 'Switch to which truck?' : 'Which truck today?'}</h3>
              <button onClick={() => setPickerOpen(false)} style={{ border: 'none', background: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            {!trucks ? (
              <div style={{ textAlign: 'center', padding: 24 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
            ) : (
              <>
                {trucks.featured.map(t => (
                  <button key={t.equipment_id} disabled={busy} onClick={() => startDay(t)}
                    style={{ ...bigBtn('#2563eb'), marginBottom: 8, justifyContent: 'space-between' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Truck size={18} /> {t.equipment_name}</span>
                    <span style={{ fontSize: 12, opacity: 0.85 }}>{t.has_tracker ? 'GPS' : 'no GPS'}</span>
                  </button>
                ))}
                <button onClick={() => setShowOthers(v => !v)}
                  style={{ ...bigBtn('#6b7280'), marginBottom: 8 }}>
                  Other truck {showOthers ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {showOthers && trucks.others.map(t => (
                  <button key={t.equipment_id} disabled={busy} onClick={() => startDay(t)}
                    style={{ ...bigBtn('#475569'), marginBottom: 6, fontSize: 14, padding: '10px 14px', justifyContent: 'space-between' }}>
                    <span>{t.bradanna_equipment_number} {t.equipment_name}</span>
                    <span style={{ fontSize: 11, opacity: 0.85 }}>{t.has_tracker ? 'GPS' : 'no GPS'}</span>
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {confirmMove && (
        <div style={overlay} onClick={() => setConfirmMove(null)}>
          <div style={sheet} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>
              {confirmMove.action === 'load' ? 'Pick up' : 'Drop off'} {confirmMove.eq.bradanna_equipment_number} {confirmMove.eq.equipment_name}?
            </h3>
            <p style={{ color: '#6b7280', fontSize: 13 }}>
              GPS will double-check this on its own — your tap just marks it right away.
            </p>
            <button style={bigBtn(confirmMove.action === 'load' ? '#2563eb' : '#d97706')} disabled={busy}
              onClick={async () => {
                const { eq, action } = confirmMove;
                setConfirmMove(null);
                await postMovement({ equipment_id: eq.equipment_id, action },
                  `${action === 'load' ? 'Pickup' : 'Drop-off'} recorded — ${eq.equipment_name}`);
                loadNearby();
              }}>
              Confirm
            </button>
          </div>
        </div>
      )}

      {rentalOpen && (
        <div style={overlay} onClick={() => setRentalOpen(false)}>
          <div style={sheet} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Rental equipment</h3>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              {(['load', 'unload'] as const).map(a => (
                <button key={a} onClick={() => setRental(r => ({ ...r, action: a }))}
                  style={{ ...bigBtn(rental.action === a ? '#7c3aed' : '#e5e7eb'), color: rental.action === a ? '#fff' : '#374151', fontSize: 14, padding: '10px' }}>
                  {a === 'load' ? 'Pick Up' : 'Drop Off'}
                </button>
              ))}
            </div>
            <input style={input} placeholder="Rental company (e.g. Sunbelt)" value={rental.company}
              onChange={e => setRental(r => ({ ...r, company: e.target.value }))} />
            <input style={input} placeholder="Equipment type (e.g. Excavator)" value={rental.type}
              onChange={e => setRental(r => ({ ...r, type: e.target.value }))} />
            <input style={input} placeholder="Description / unit #" value={rental.description}
              onChange={e => setRental(r => ({ ...r, description: e.target.value }))} />
            <button style={{ ...bigBtn('#7c3aed'), opacity: busy ? 0.6 : 1 }} disabled={busy}
              onClick={async () => {
                if (!rental.company.trim()) { say('Enter the rental company first', true); return; }
                const ok = await postMovement({
                  action: rental.action, is_rental: true, rental_company: rental.company,
                  rental_equipment_type: rental.type || undefined, rental_description: rental.description || undefined,
                }, 'Rental recorded');
                if (ok) {
                  setRentalOpen(false);
                  setRental({ action: 'load', company: '', type: '', description: '' });
                }
              }}>
              {busy ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : 'Save'}
            </button>
          </div>
        </div>
      )}

      {miscOpen && (
        <div style={overlay} onClick={() => setMiscOpen(false)}>
          <div style={sheet} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Parts / materials run</h3>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              {(['load', 'unload'] as const).map(a => (
                <button key={a} onClick={() => setMisc(m => ({ ...m, action: a }))}
                  style={{ ...bigBtn(misc.action === a ? '#0891b2' : '#e5e7eb'), color: misc.action === a ? '#fff' : '#374151', fontSize: 14, padding: '10px' }}>
                  {a === 'load' ? 'Pick Up' : 'Drop Off'}
                </button>
              ))}
            </div>
            <input style={input} placeholder="What is it? (e.g. culvert pipe, filters)" value={misc.description}
              onChange={e => setMisc(m => ({ ...m, description: e.target.value }))} />
            <input style={input} placeholder="Notes (optional)" value={misc.notes}
              onChange={e => setMisc(m => ({ ...m, notes: e.target.value }))} />
            <button style={{ ...bigBtn('#0891b2'), opacity: busy ? 0.6 : 1 }} disabled={busy}
              onClick={async () => {
                if (!misc.description.trim()) { say('Describe what it is first', true); return; }
                const ok = await postMovement({
                  action: misc.action, is_rental: true, rental_company: 'MISC',
                  rental_description: misc.description, notes: misc.notes || undefined,
                }, 'Recorded');
                if (ok) {
                  setMiscOpen(false);
                  setMisc({ action: 'load', description: '', notes: '' });
                }
              }}>
              {busy ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : 'Save'}
            </button>
          </div>
        </div>
      )}

      {dumpOpen && (
        <div style={overlay} onClick={() => setDumpOpen(false)}>
          <div style={sheet} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Dump loads</h3>
            <p style={{ margin: '0 0 8px', fontSize: 12, color: '#6b7280' }}>
              Job = who gets billed. Hauling OFF a job (to the farm/pit)? Pick the job you hauled off of.
            </p>
            <select style={input} value={dump.job_id} onChange={e => setDump(d => ({ ...d, job_id: e.target.value }))}>
              <option value="">Job…</option>
              {jobs.map(j => <option key={j.job_id} value={j.job_id}>{j.job_name}</option>)}
            </select>
            <input style={input} placeholder="Material (e.g. fill, 57 stone, millings)" value={dump.material}
              onChange={e => setDump(d => ({ ...d, material: e.target.value }))} />
            <input style={input} type="number" min="1" placeholder="Load count" value={dump.count}
              onChange={e => setDump(d => ({ ...d, count: e.target.value }))} />
            <input style={input} placeholder="Notes (optional)" value={dump.notes}
              onChange={e => setDump(d => ({ ...d, notes: e.target.value }))} />
            <button style={{ ...bigBtn('#65a30d'), opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={submitDump}>
              {busy ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Custom bottom nav (truck driver only — same slots as the old dashboard) */}
      {isTrucker && (
        <nav style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff',
          borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-around',
          alignItems: 'center', padding: '8px 0 12px', zIndex: 900,
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
        }}>
          {[
            { icon: <HomeIcon size={24} />, label: 'Home', color: '#3b82f6', go: () => window.scrollTo({ top: 0, behavior: 'smooth' }) },
            { icon: <Calendar size={24} />, label: 'Weekly Hrs', color: '#6b7280', go: openWeekly },
            { icon: <AlertTriangle size={24} />, label: 'Report Issue', color: '#ef4444', go: () => navigate('/equipment') },
            { icon: <Briefcase size={24} />, label: 'Jobs', color: '#10b981', go: () => navigate('/cdl-jobs') },
          ].map(b => (
            <button key={b.label} onClick={b.go} style={{
              background: 'none', border: 'none', display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 4, padding: '8px 12px', cursor: 'pointer', color: b.color,
            }}>
              {b.icon}
              <span style={{ fontSize: 11, fontWeight: 600 }}>{b.label}</span>
            </button>
          ))}
        </nav>
      )}

      {/* Weekly hours modal */}
      {weeklyOpen && (
        <div style={overlay} onClick={() => setWeeklyOpen(false)}>
          <div style={sheet} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h3 style={{ margin: 0 }}>This week</h3>
              <button onClick={() => setWeeklyOpen(false)} style={{ border: 'none', background: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            {weeklyLoading ? (
              <div style={{ textAlign: 'center', padding: 24 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
            ) : !weekly ? (
              <p style={{ color: '#6b7280', fontSize: 14 }}>Could not load weekly hours.</p>
            ) : (
              <>
                {weekly.dailyHours.length === 0 && <p style={{ color: '#6b7280', fontSize: 14 }}>No completed days yet this week.</p>}
                {weekly.dailyHours.map(d => (
                  <div key={String(d.work_date)} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 4px', borderBottom: '1px solid #f3f4f6', fontSize: 15 }}>
                    <span>{new Date(d.work_date).toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric', timeZone: 'UTC' })}</span>
                    <span style={{ fontWeight: 700 }}>{Number(d.total_hours).toFixed(2)} hrs</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 4px 4px', fontSize: 16, fontWeight: 800 }}>
                  <span>Week total</span>
                  <span>{Number(weekly.weekTotal).toFixed(2)} hrs</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 1100,
          background: toast.err ? '#dc2626' : '#16a34a', color: '#fff', padding: '10px 18px',
          borderRadius: 999, fontSize: 14, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
          maxWidth: '90%',
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
};
